import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Axios from "axios";
import moment from "moment-timezone";
import ReactEcharts from "echarts-for-react";

import { useFormatter } from "./../../util/formatter";
import { useRouter } from "./../../util/router";
import { useSite } from "./../../util/sites";
import { useSolarCalc } from "./../../util/calculations";
import { useUser } from "../../util/user";
import GYAxios from "../../util/GYAxios";
import usePeakLabels from "../../util/peakLabels";

import AnalyticDateRangePicker from "./AnalyticDateRangePicker";
import AnalyticIndeGraphs from "./AnalyticIndeGraphs";
import AnalyticAllDataGraph from './AnalyticAllDataGraph';
import AnalyticProductionDataGraph from "./AnalyticProductionDataGraph";
import LoaderGraph from "../general/LoaderGraph";

import { BESS_PARAMS_ENUM, get_BESS_col_name, combine_cabinet_data } from "../bess/bess_func";

import styles from './AnalyticContents.module.css';

const isDohomeCombinedTable = (tableName) => /^dohome_[a-z0-9_]+_combined$/i.test(tableName || "");

// util functions moved to here, to prevent infinite rendering.
const solarCalc = useSolarCalc();
const [formatNumber] = useFormatter();

function AnalyticContents() {
	const currentSite = useSite();
	const router = useRouter();
	const user = useUser();

	const operationStatusDate = useMemo(() => {
		const operationStatus = currentSite?.selectedSite?.value?.operation_status;
		const msaDate = currentSite?.selectedSite?.value?.msa;
		const codDate = currentSite?.selectedSite?.value?.cod;
		return (
			<div style={{ marginTop: '2em', textAlign: 'center', fontSize: '1.2em', lineHeight: '1' }}>
				{operationStatus === 1 && <div style={{ marginBottom: '0.5em' }}>License in progress</div>}
				{!!msaDate && operationStatus === 2 && <div style={{ marginBottom: '0.5em' }}>MSA: {moment(msaDate).format('MMMM D, YYYY')}</div>}
				{!!codDate && operationStatus === 3 && <div style={{ marginBottom: '0.5em' }}>COD: {moment(codDate).format('MMMM D, YYYY')}</div>}
			</div>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentSite?.selectedSite?.value]);

	const isBESS = currentSite?.selectedSite?.value?.is_BESS;

	const [dataIndeDaily, setDataIndeDaily] = useState(null);
	const [dataIndeMonthly, setDataIndeMonthly] = useState(null);
	const [P10DataReady, setP10DataReady] = useState(false);
	const [dataReq_P10, setDataReq_P10] = useState(null);
	const [dataReq_BESS, setDataReq_BESS] = useState(null);
	const [dataReq_injPower, setDataReq_injPower] = useState(null);
	const [data_P10, setData_P10] = useState(null);
	const [mainGraphConfig, setMainGraphConfig] = useState(null);
	const [phaseGraphConfig, setPhaseGraphConfig] = useState(null);
	const [selectedSite, setSelectedSite] = useState(null);
	const [period, setPeriod] = useState({
		fromDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
		toDate: moment().format("YYYY-MM-DD"),
	});

	const roles = user.roles;
	const isGYUser = roles.includes("_GY:Base");
	const isClient = roles.includes("_GY:Client");
	const solarLabel = usePeakLabels(currentSite.selectedSite.value.country);

	// DAILY DATA & BRUT PR
	const [dataReq_daily, setDataReq_daily] = useState(null);
	const [data_daily, setData_daily] = useState(null);
	const [dailyGraphConfig, setDailyGraphConfig] = useState(null);
	const [dailyDataReady, setDailyDataReady] = useState(false);
	const [PRGraphConfig, setPRGraphConfig] = useState(null);
	// END DAILY DATA & BRUT PR

	// SENSOR DATA & PRODUCTION (TOGGLE)
	const toggleCharts = useMemo(() => ({ sensorChart: 'SENSOR_CHART', productionChart: 'PRODUCTION_CHART' }), []);
	const [selectedChart, setSelectedChart] = useState(toggleCharts.sensorChart);
	// END SENSOR DATA & PRODUCTION (TOGGLE)

	// Inj power cols
	const isInjPowerCol = col => /^inj_\d{2}_power$/i.test(col);

	/**
	 * Get array of months
	 */
	const getMonthArr = (size, monthNumber, arr, plant, cumul) => {
		let tempIrrad = [];

		for (let index = 0; index < size; index++) {
			const irrad = parseFloat(arr[monthNumber - 1][plant]);
			tempIrrad.push(irrad);

			if (monthNumber === 12) monthNumber = 1;
			else monthNumber++;
		}

		if (cumul) {
			let count = 0;
			let tempIrradCumul = [];
			tempIrrad.map((reading) => {
				count += reading;
				tempIrradCumul.push(count);
			});
			tempIrrad = tempIrradCumul;
		}

		tempIrrad = tempIrrad.map((number) => {
			return number.toFixed(2);
		});

		return tempIrrad;
	};

	/**
	 * Get P10 data of selected site
	 * @param {Selected Site} selectedSite
	 */
	const getDataP10 = useCallback(() => {
		const fromDate = period?.fromDate;
		const toDate = period?.toDate;

		const tableName = selectedSite?.value?.table_name;
		const isDohomeCombined = isDohomeCombinedTable(tableName);
		const siteID = selectedSite.value.site_id;

		if (!fromDate || !toDate || !tableName) { return }

		// API request URIs
		const p10DataURI = `/p10/analytics/site/${tableName}/period/${fromDate}/${toDate}`;
		const injPowerDataURI = `/p10/analytics/site/${tableName}/period/${fromDate}/${toDate}/injection-point-power`;
		const dailyDataURI = `/daily/site/${siteID}/period/${fromDate}/${toDate}/order/dateData/ASC`;
		const bessP10DataURI = `/bess/sites/${tableName}/p10/${fromDate}/${toDate}`;

		// Fetch data and set data into state
		GYAxios.get(p10DataURI).then((res) => {
			const p10Data = res.data.error == null ? res.data : [];
			setDataReq_P10(p10Data);
		});

		if (isDohomeCombined) {
			GYAxios.get(injPowerDataURI).then((res) => {
				const injPowerData = res.data.error == null ? res.data : [];
				setDataReq_injPower(injPowerData);
			}).catch(() => {
				setDataReq_injPower([]);
			});
		} else {
			setDataReq_injPower([]);
		}

		// fetch Daily data & Actual PR
		GYAxios.get(dailyDataURI).then((res) => {
			const dailyData = res.data.error == null ? res.data : [];
			setDataReq_daily(dailyData);
		});

		// BESS data
		if(isBESS) {
			GYAxios.get(bessP10DataURI).then((res) => {
				const p10BESSData = !res.data.error ? res.data?.data : [];
				setDataReq_BESS(p10BESSData);
			});
		}
	}, [period, selectedSite, isBESS]);

	/**
	 * Get data based on data of date range picker
	 */
	const getData_DRP = useCallback(process => {
		if (process === "P10") {
			let graphDataObj_P10 = {
				dates: [],
				loadProfile: [],
				solar_power: [],
				grid_power: [],
				gridPhase1: [],
				gridPhase2: [],
				gridPhase3: [],
				irr: [],
				injPowerData: {},
			};

			if(currentSite?.selectedSite?.value?.is_BESS) {
				graphDataObj_P10['bess_power'] = [];
			}

			dataReq_P10.forEach((row) => {
				// Format Date
				const date = moment(row.dateData).format("DD/MM HH:mm");
				graphDataObj_P10.dates.push(date);

				const loadProfile = solarCalc.parseFormatNumber(
					row.solar_power + row.grid_power,
					3
				);
				// Assign values retrieved to state object
				graphDataObj_P10.loadProfile.push(loadProfile);
				graphDataObj_P10.solar_power.push(row.solar_power);
				graphDataObj_P10.grid_power.push(row.grid_power);
				graphDataObj_P10.irr.push(row.irradiance);
				graphDataObj_P10.gridPhase1.push(row.grid_phase1);
				graphDataObj_P10.gridPhase2.push(row.grid_phase2);
				graphDataObj_P10.gridPhase3.push(row.grid_phase3);
			});

			if(currentSite?.selectedSite?.value?.is_BESS && dataReq_BESS) {
				dataReq_BESS.map(row => combine_cabinet_data(row, currentSite?.selectedSite?.value?.BESS_nb_cabinets)).forEach((row) => {
					graphDataObj_P10.bess_power.push(row[get_BESS_col_name('ALL', BESS_PARAMS_ENUM.POWER)]);
				});
			}

			if (isDohomeCombinedTable(currentSite?.selectedSite?.value?.table_name) && Array.isArray(dataReq_injPower) && dataReq_injPower.length > 0) {
				const injPowerCols = Object.keys(dataReq_injPower[0])
					.filter(isInjPowerCol)
					.sort();
				const injPowerByDate = new Map(
					dataReq_injPower.map((row) => [moment(row.dateData).format("YYYY-MM-DD HH:mm:ss"), row])
				);

				dataReq_P10.forEach((row) => {
					const injRow = injPowerByDate.get(moment(row.dateData).format("YYYY-MM-DD HH:mm:ss")) || {};
					injPowerCols.forEach((col) => {
						if(!graphDataObj_P10.injPowerData[col]) {
							graphDataObj_P10.injPowerData[col] = [];
						}
						graphDataObj_P10.injPowerData[col].push(injRow[col]);
					});
				});
			}

			setData_P10(graphDataObj_P10);
		}
		if (process === 'DAILY') {
			const data_daily = {
				dates: [],
				solarON: [],
				solarOFF: [],
				solarTotal: [],
				gridTotal: [],
				irr: [],
				PR: [],
				module_temp: [],
				ambient_temp: [],
			};

			dataReq_daily.map((resRow) => {
				let brut_PR = resRow.brut_PR;
				if (brut_PR > 100) { brut_PR = 100 }
				data_daily.dates.push(moment(resRow.dateData).format("DD/MM"));
				data_daily.module_temp.push(
					formatNumber(resRow.avg_temp_module, 2)
				);
				data_daily.ambient_temp.push(
					formatNumber(resRow.avg_temp_ambient, 2)
				);
				data_daily.irr.push(resRow.irradiance_sum / 6000);
				data_daily.PR.push(parseFloat(formatNumber(brut_PR, 2)));
				data_daily.solarON.push(resRow.solar_ON);
				data_daily.solarTotal.push(resRow.solar_total);
				data_daily.solarOFF.push(resRow.solar_OFF);
				data_daily.gridTotal.push(resRow.grid_total);
				setData_daily(data_daily);
			});
		}
	}, [currentSite.selectedSite.value.BESS_nb_cabinets, currentSite.selectedSite.value.is_BESS, currentSite?.selectedSite?.value?.table_name, dataReq_BESS, dataReq_P10, dataReq_daily, dataReq_injPower]);

	/**
	 * Get data for other graphs
	 */
	const getData_inde = useCallback(() => {
		if (!selectedSite?.value) { return }
		if (selectedSite.value.cod != null) {
			let COD_obj = moment(selectedSite.value.cod);
			const tableName = selectedSite.value.table_name;

			const date = COD_obj.format("DD");
			let COD = COD_obj.format("YYYY-MM-DD");

			if (date !== "01")
				COD = COD_obj.add(1, "months").startOf("month").format("YYYY-MM-DD");

			const toDate = moment()
				.subtract(1, "months")
				.endOf("month")
				.format("YYYY-MM-DD");

			const period = {
				fromDate: COD,
				toDate,
			};

			let indeGraphObj_monthly = {
				months: [],
				solar: [],
				gar_target: [],
				exp_target: [],
				irrad_cumul: [],
				solar_cumul: [],
				gar_target_cumul: [],
				exp_target_cumul: [],
				irrad_target_cumul: [],
				irr: [],
				irr_target: [],
			};

			let indeGraphObj_daily = {
				dates: [],
				irrad_cumul: [0],
				tgp_cumul: [0],
				tep_cumul: [0],
				solar_cumul: [0],
				irr_exp_cumul: [0],
			};

			const param_COD = {
				table_name: selectedSite.value.table_name,
				duration: period,
				process: "COD",
			};

			const reqTargetProdMonthly = GYAxios.post(
				"/site/get_target_production",
				param_COD
			);

			const reqTargetIrrMonthly = GYAxios.get(
				`/site/target_irradiance/${tableName}`,
				{
					month_id: null,
				}
			);

			Axios.all([reqTargetProdMonthly, reqTargetIrrMonthly])
				.then(
					Axios.spread((...responses) => {
						const resTargetProd = responses[0];
						const resTargetIrr = responses[1];
						const size = resTargetProd.data.length;
						if (resTargetProd.data != null && resTargetProd.data.length > 0) {
							const monthNumber = parseFloat(
								moment(resTargetProd.data[0].date).format("M")
							);

							let solar_cumul = 0;
							let irrad_cumul = 0;
							let gar_target_cumul = 0;
							let exp_target_cumul = 0;

							resTargetProd.data.map((row) => {
								solar_cumul += row.solar;
								irrad_cumul += solarCalc.calculateIrr(row.irr);
								gar_target_cumul += row.gar_target;
								exp_target_cumul += row.exp_target;

								indeGraphObj_monthly.months.push(
									moment(row.date).format("MMM 'YY")
								);
								indeGraphObj_monthly.solar.push(Math.round(row.solar));
								indeGraphObj_monthly.gar_target.push(
									Math.round(row.gar_target)
								);
								indeGraphObj_monthly.exp_target.push(
									Math.round(row.exp_target)
								);
								indeGraphObj_monthly.solar_cumul.push(Math.round(solar_cumul));
								indeGraphObj_monthly.gar_target_cumul.push(
									Math.round(gar_target_cumul)
								);
								indeGraphObj_monthly.exp_target_cumul.push(
									Math.round(exp_target_cumul)
								);
								indeGraphObj_monthly.irr.push(
									parseFloat(solarCalc.calculateIrr(row.irr).toFixed(2))
								);
								indeGraphObj_monthly.irrad_cumul.push(
									parseFloat(irrad_cumul.toFixed(2))
								);
							});

							const irradArr = getMonthArr(
								size,
								monthNumber,
								resTargetIrr.data,
								selectedSite.value.table_name,
								false
							);

							const irradCumulArr = getMonthArr(
								size,
								monthNumber,
								resTargetIrr.data,
								selectedSite.value.table_name,
								true
							);

							indeGraphObj_monthly.irr_target = irradArr;
							indeGraphObj_monthly.irrad_target_cumul = irradCumulArr;
							setDataIndeMonthly(indeGraphObj_monthly);
						} else setDataIndeMonthly(null);
					})
				)
				.catch((error) => {
					console.log(
						"error reqTargetProdMonthly, reqTargetIrrMonthly: ",
						error
					);
					setDataIndeMonthly(null);
				});

			const lastDay = parseFloat(moment().endOf("month").format("DD"));
			const monthNumber = parseFloat(moment().startOf("month").format("M"));

			const param_CURRMONTH = {
				table_name: selectedSite.value.table_name,
				duration: {
					fromDate: moment().startOf("month").format("YYYY-MM-DD"),
				},
				process: "CURMONTH",
			};

			const paramTargetIrrDaily = {
				month_id: monthNumber,
			};

			const reqTargetProdDaily = GYAxios.post(
				"/site/get_target_production",
				param_CURRMONTH
			);

			const reqTargetIrrDaily = GYAxios.get(
				`/site/target_irradiance/${tableName}`,
				paramTargetIrrDaily
			);

			Axios.all([reqTargetProdDaily, reqTargetIrrDaily])
				.then(
					Axios.spread((...responses) => {
						const resTargetProdDaily = responses[0];
						const resTargetIrrDaily = responses[1];

						if (resTargetProdDaily.data != null) {
							let irrad_cumul = 0;
							let tgp_cumul = 0;
							let tep_cumul = 0;
							let solar_cumul = 0;

							const tgpStep = resTargetProdDaily.data[0].gar_target / lastDay;
							const tepStep = resTargetProdDaily.data[0].exp_target / lastDay;

							for (let i = 0; i <= lastDay; i++) {
								if (i < 10) i = "0" + i;

								tgp_cumul += tgpStep;
								tep_cumul += tepStep;
								indeGraphObj_daily.dates.push(i + "");
								indeGraphObj_daily.tgp_cumul.push(tgp_cumul.toFixed(0));
								indeGraphObj_daily.tep_cumul.push(tep_cumul.toFixed(0));
							}

							resTargetProdDaily.data.map((row) => {
								irrad_cumul += row.irr / 6000;
								solar_cumul += row.solar;
								indeGraphObj_daily.irrad_cumul.push(irrad_cumul.toFixed(2));
								indeGraphObj_daily.solar_cumul.push(solar_cumul.toFixed(0));
							});

							const irrStep =
								resTargetIrrDaily.data[0][selectedSite.value.table_name] /
								lastDay;
							for (let index = 1; index <= lastDay; index++) {
								indeGraphObj_daily.irr_exp_cumul.push(
									(irrStep * index).toFixed(2)
								);
							}

							setDataIndeDaily(indeGraphObj_daily);
						} else setDataIndeDaily(null);
					})
				)
				.catch((error) => {
					console.log("error reqTargetProdDaily, reqTargetIrrDaily: ", error);
					setDataIndeDaily(null);
				});
		} else {
			setDataIndeMonthly(null);
			setDataIndeDaily(null);
		}
	}, [selectedSite]);

	/**
	 * Handle change of period of dataa
	 */
	const handleEvent = useCallback((event, picker) => {
		const duration_obj = {
			fromDate: picker.startDate.format("YYYY-MM-DD"),
			toDate: picker.endDate.format("YYYY-MM-DD"),
		};
		setPeriod(duration_obj);
	}, []);

	useEffect(() => {
		const siteID = sessionStorage.getItem("site_id");

		if (
			currentSite.selectedSite.label === currentSite.defaultSelect.label &&
			siteID == null
		)
			router.push("/solar/overview");
		if (
			(currentSite.selectedSite != null && selectedSite == null) ||
			(selectedSite != null &&
				selectedSite?.value?.table_name !== currentSite.selectedSite?.value?.table_name)
		) {
			const site = currentSite.selectedSite;
			setSelectedSite(site);
		}
	}, [currentSite.defaultSelect.label, currentSite.selectedSite, router, selectedSite]);

	useEffect(() => {
		if (!selectedSite) { return }

		setP10DataReady(false);
		setDailyDataReady(false);
		getDataP10();
		getData_inde();

		const timer = setInterval(() => {
			getDataP10();
			getData_inde();
		}, 600000);

		return () => {
			clearInterval(timer);
		};
	}, [selectedSite, getDataP10, getData_inde]);

	useEffect(() => {
		if(currentSite?.selectedSite) {
			const isDohomeCombined = isDohomeCombinedTable(currentSite?.selectedSite?.value?.table_name);
			if((currentSite?.selectedSite?.value?.is_BESS && dataReq_BESS && dataReq_P10) ||
				(!currentSite?.selectedSite?.value?.is_BESS && dataReq_P10)) {
				if (isDohomeCombined && dataReq_injPower == null) {
					return;
				}
				getData_DRP("P10");
			}
		}
	}, [currentSite, dataReq_P10, getData_DRP, dataReq_BESS, dataReq_injPower]);

	useEffect(() => {
		if (dataReq_daily != null) getData_DRP("DAILY");
	}, [dataReq_daily, getData_DRP]);

	useEffect(() => {

		if (data_P10 != null) {
			const injPowerCols = Object.keys(data_P10.injPowerData).filter(isInjPowerCol);
			const hasInjPower = injPowerCols.length > 0;
			const injLegend = hasInjPower
				? injPowerCols.map((col, index) => index === 0 ? "GY Plant Power" : `Phase ${parseInt(/inj_(\d{2})_power/i.exec(col)?.[1])-1} Power`)
				: [];
			const optionkW = {
				title: {
					text: "Live Data",
				},
				tooltip: {
					trigger: "axis",
					axisPointer: {
						animation: true,
						type: "cross",
					},
				},
				legend: {
					data: ["Load Profile", ...(hasInjPower ? injLegend : ["Solar Power"]), "Grid Power", "Irradiance", "BESS Power"],
					selected: {
						"Load Profile": false,
						...(hasInjPower
							? Object.fromEntries(injLegend.map((name) => [name, true]))
							: { "Solar Power": true }),
						"Grid Power": true,
						"Irradiance": true,
					},
				},
				toolbox: {
					show: true,
					orient: "horizontal",
					left: "right",
					top: "top",
					feature: {
						magicType: {
							show: true,
							title: {
								line: "",
								bar: "",
								stack: "",
							},
							type: ["line", "bar"],
							iconStyle: {
								textPosition: "top",
							},
						},
						restore: {
							show: true,
							title: " ",
						},
						saveAsImage: {
							show: true,
							title: " ",
						},
					},
				},
				dataZoom: [
					{
						type: "slider",
						filterMode: "filter",
						start: 90,
						end: 100,
					},
					{
						type: "inside",
						filterMode: "filter",
						realtime: true,
						start: 90,
						end: 100,
					},
				],
				grid: {
					left: 70,
					right: 100,
					top: 80,
					bottom: 70,
				},
				xAxis: [
					{
						type: "category",
						axisTick: {
							alignWithLabel: true,
						},
						data: data_P10.dates,
					},
				],
				yAxis: [
					{
						type: "value",
						name: "Power",
						axisLabel: {
							formatter: "{value} kW",
						},
						axisLine: {
							show: true,
						},
						axisTick: {
							show: true,
						},
						min: "dataMin",
					},
					{
						type: "value",
						name: "Irradiance",
						axisLabel: {
							formatter: "{value} W/m\xB2",
						},
						axisLine: {
							show: true,
						},
						axisTick: {
							show: true,
						},
					},
				],
				series: [
					{
						name: "Load Profile",
						type: "line",
						areaStyle: {},
						lineStyle: {
							opacity: 0,
						},
						itemStyle: {
							color: "#f8de8b",
						},
						symbolSize: 2,
						showSymbol: false,
						data: data_P10.loadProfile,
					},
					...(hasInjPower
						? injPowerCols.map((col, index) => ({
							name: index === 0 ? "GY Plant Power" : `Phase ${parseInt(/inj_(\d{2})_power/i.exec(col)?.[1])-1} Power`,
							type: "line",
							lineStyle: {
								color: "#ffc602",
								normal: {
									width: 1.2,
								},
							},
							itemStyle: {
								color: "#ffc602",
							},
							symbolSize: 2,
							showSymbol: false,
							data: data_P10.injPowerData[col] || [],
						}))
						: [{
							name: "Solar Power",
							type: "line",
							lineStyle: {
								color: "#ffc602",
								normal: {
									width: 1.2,
								},
							},
							itemStyle: {
								color: "#ffc602",
							},
							symbolSize: 2,
							showSymbol: false,
							data: data_P10.solar_power,
						}]),
					{
						name: "Grid Power",
						type: "line",
						lineStyle: {
							color: "#f28a10",
							normal: {
								width: 1.2,
							},
						},
						itemStyle: {
							color: "#f28a10",
						},
						symbolSize: 2,
						showSymbol: false,
						data: data_P10.grid_power,
					},
					{
						name: "Irradiance",
						type: "line",
						lineStyle: {
							color: "#86bc25",
							normal: {
								width: 1.2,
							},
						},
						itemStyle: {
							color: "#86bc25",
						},
						yAxisIndex: 1,
						symbolSize: 2,
						showSymbol: false,
						data: data_P10.irr,
					},
					...(currentSite?.selectedSite?.value?.is_BESS ? [{
						name: "BESS Power",
						type: "line",
						lineStyle: {
							color: '#1890ff',
							normal: {
								width: 1.2,
							},
						},
						itemStyle: {
							color: '#1890ff'
						},
						symbolSize: 2,
						showSymbol: false,
						data: data_P10.bess_power,
					}] : []),
				],
			};

			const optionPhase = {
				title: {
					text: "Grid power per phase",
				},
				tooltip: {
					trigger: "axis",
					axisPointer: {
						animation: true,
						type: "cross",
					},
				},
				legend: {
					data: ["Phase 1", "Phase 2", "Phase 3"],
				},
				toolbox: {
					show: true,
					orient: "horizontal",
					left: "right",
					top: "top",
					feature: {
						magicType: {
							show: true,
							title: {
								line: "",
								bar: "",
								stack: "",
							},
							type: ["line", "bar", "stack"],
							iconStyle: {
								textPosition: "top",
							},
						},
						restore: {
							show: true,
							title: " ",
						},
						saveAsImage: {
							show: true,
							title: " ",
						},
					},
				},
				dataZoom: [
					{
						type: "slider",
						filterMode: "filter",
						start: 90,
						end: 100,
					},
					{
						type: "inside",
						filterMode: "filter",
						realtime: true,
						start: 90,
						end: 100,
					},
				],
				grid: {
					left: 70,
					right: 100,
					top: 80,
					bottom: 70,
				},
				xAxis: [
					{
						type: "category",
						axisTick: {
							alignWithLabel: true,
						},
						data: data_P10.dates,
					},
				],
				yAxis: [
					{
						type: "value",
						name: "Power",
						axisLabel: {
							formatter: "{value} kW",
						},
						axisLine: {
							show: true,
						},
						axisTick: {
							show: true,
						},
					},
				],
				series: [
					{
						name: "Phase 1",
						type: "line",
						lineStyle: {
							normal: {
								width: 1.2,
							},
						},
						symbolSize: 2,
						showSymbol: false,
						data: data_P10.gridPhase1,
					},
					{
						name: "Phase 2",
						type: "line",
						lineStyle: {
							normal: {
								width: 1.2,
							},
						},
						symbolSize: 2,
						showSymbol: false,
						data: data_P10.gridPhase2,
					},
					{
						name: "Phase 3",
						type: "line",
						lineStyle: {
							normal: {
								width: 1.2,
							},
						},
						symbolSize: 2,
						showSymbol: false,
						data: data_P10.gridPhase3,
					},
				],
			};

			setMainGraphConfig(optionkW);
			setPhaseGraphConfig(optionPhase);
			setP10DataReady(true);
		}
	}, [currentSite.selectedSite.value.is_BESS, data_P10]);

	useEffect(() => {
		const colors2 = [
			"#ffc602",
			"#f28a10",
			"#12a8e0",
			"#86bc25",
			"#c23531",
			"#9543e0",
		];

		if (data_daily != null) {
			const optionDD = {
				title: {
					text: "Daily Data",
				},
				grid: {
					top: 120,
					right: "15%",
				},
				color: colors2,
				tooltip: {
					trigger: "axis",
					axisPointer: {
						type: "shadow",
					},
					formatter: function (params) {
						var i = 0;
						var total = 0;
						var solar_total = 0;

						var solar_on_selected = false;
						var solar_off_selected = false;
						var solar_grid_selected = false;

						var colorON = "rgb(136,185,23, 0)";
						var colorOFF = "rgb(136,185,23, 0)";
						var colorGrid = "rgb(136,185,23, 0)";
						var colorSpan = (color) =>
							'<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:' +
							color +
							'"></span>';
						let rez = "</br>";

						params.forEach((item) => {
							var xx;
							if (item.seriesName !== "Irradiation") {
								if (
									item.seriesName === solarLabel[0] ||
									item.seriesName === solarLabel[1]
								) {
									solar_total += +parseFloat(item.data).toFixed(2);

									switch (item.seriesName) {
										case solarLabel[0]:
											colorON = item.color;
											solar_on_selected = true;

											break;
										case solarLabel[1]:
											colorOFF = item.color;
											solar_off_selected = true;
											break;
										default:
											break;
									}
								} else {
									if (item.seriesName === "Grid consumption") {
										colorGrid = item.color;
										solar_grid_selected = true;
									}
								}

								if (
									item.seriesName !== "Module Temp" &&
									item.seriesName !== "Ambient Temp"
								) {
									total += +parseFloat(item.data).toFixed(2);
								}

								if (
									item.seriesName === "Module Temp" ||
									item.seriesName === "Ambient Temp"
								) {
									xx =
										"<p>" +
										colorSpan(item.color) +
										" " +
										params[i].axisValue +
										" " +
										item.seriesName +
										": " +
										parseFloat(item.data).toFixed(2) +
										" °C" +
										"</p>";
								} else {
									xx =
										"<p>" +
										colorSpan(item.color) +
										" " +
										params[i].axisValue +
										" " +
										item.seriesName +
										": " +
										parseFloat(item.data).toFixed(2) +
										" kWh" +
										"</p>";
								}
								rez += xx;
							} else {
								xx =
									"<p>" +
									colorSpan(item.color) +
									" " +
									params[i].axisValue +
									" " +
									item.seriesName +
									": " +
									parseFloat(item.data).toFixed(2) +
									" kWh/m\xB2" +
									"</p>";
								rez += xx;
							}
							i++;
						});

						switch (true) {
							case solar_grid_selected:
								if (solar_on_selected && solar_off_selected) {
									rez += "<hr style='padding:0; margin:10px 0'/>";
									rez +=
										"<p>" +
										colorSpan(colorON) +
										colorSpan(colorOFF) +
										"Total solar production: <b>" +
										solar_total.toFixed(2) +
										" kWh</b></p>";
									rez +=
										"<p>" +
										colorSpan(colorON) +
										colorSpan(colorOFF) +
										colorSpan(colorGrid) +
										"Total consumption: <b>" +
										total.toFixed(2) +
										" kWh</b></p>";
								} else if (solar_on_selected || solar_off_selected) {
									rez += "<hr style='padding:0; margin:10px 0'/>";
									rez +=
										"<p>" +
										colorSpan(colorON) +
										colorSpan(colorOFF) +
										colorSpan(colorGrid) +
										"Total selected consumption: <b>" +
										total.toFixed(2) +
										" kWh</b></p>";
								} else rez += "<br/>";
								break;

							default:
								if (solar_on_selected && solar_off_selected) {
									rez += "<hr style='padding:0; margin:10px 0'/>";
									rez +=
										"<p>" +
										colorSpan(colorON) +
										colorSpan(colorOFF) +
										"Total solar production: <b>" +
										solar_total.toFixed(2) +
										" kWh</b></p>";
								} else rez += "<br/>";
								break;
						}
						return rez;
					},
				},
				legend: {
					data: [
						solarLabel[0],
						solarLabel[1],
						"Grid consumption",
						"Irradiation",
						"Module Temp",
						"Ambient Temp",
					],
					top: "7%",
					selected: {
						"Module Temp": false,
					},
				},
				toolbox: {
					show: true,
					left: "right",
					feature: {
						magicType: {
							show: true,
							title: {
								line: "",
								bar: "",
								stack: "",
								tiled: "",
							},
							type: ["bar", "tiled"],
							iconStyle: {
								textPosition: "top",
							},
						},
						restore: {
							show: true,
							title: " ",
						},
						saveAsImage: {
							show: true,
							title: " ",
						},
					},
				},
				xAxis: [
					{
						type: "category",
						axisTick: {
							show: false,
						},
						data: data_daily.dates,
					},
				],
				yAxis: [
					{
						type: "value",
						name: "Energy",
						axisLabel: {
							formatter: "{value} kWh",
						},
						axisLine: { show: true },
						axisTick: {
							show: true,
						},
						splitLine: false,
					},
					{
						type: "value",
						name: "Irradiation",
						axisLabel: {
							formatter: "{value} kWh/m\xB2",
						},
						axisLine: { show: true },
						axisTick: {
							show: true,
						},
						splitLine: false,
					},
					{
						type: "value",
						name: "Temp",
						axisLabel: {
							formatter: "{value} °C",
						},
						axisLine: { show: true },
						axisTick: {
							show: true,
						},
						splitLine: false,
						offset: 80,
					},
				],
				series: [
					{
						name: solarLabel[0],
						type: "bar",
						stack: "总量",
						barGap: 0,
						data: data_daily.solarON,
					},
					{
						name: solarLabel[1],
						type: "bar",
						stack: "总量",
						barGap: 0,
						data: data_daily.solarOFF,
					},
					{
						name: "Grid consumption",
						type: "bar",
						stack: "总量",
						data: data_daily.gridTotal,
					},
					{
						name: "Irradiation",
						type: "line",
						yAxisIndex: 1,
						data: data_daily.irr,
					},
					{
						name: "Module Temp",
						type: "line",
						yAxisIndex: 2,
						data: data_daily.module_temp,
					},
					{
						name: "Ambient Temp",
						type: "line",
						yAxisIndex: 2,
						data: data_daily.ambient_temp,
					},
				],
			};

			const optionPr = {
				title: {
					text: "Actual Performance Ratio",
					left: "left",
				},
				grid: {
					top: "20%",
				},
				color: ["#c23531"],
				tooltip: {
					trigger: "axis",
					axisPointer: {
						type: "cross",
						crossStyle: {
							color: "#999",
						},
					},
				},
				toolbox: {
					feature: {
						saveAsImage: {
							show: true,
							title: "Save",
						},
					},
				},
				xAxis: [
					{
						type: "category",
						name: "Date",
						data: data_daily.dates,
						axisPointer: {
							type: "none",
						},
					},
				],
				yAxis: [
					{
						type: "value",
						name: "Percentage",
						interval: 50,
						min: 0,
						max: 100,
						axisLabel: {
							formatter: "{value} %",
						},
						axisLine: {
							show: true,
						},
						axisTick: {
							show: true,
						},
					},
				],
				series: [
					{
						name: "Performance Ratio",
						type: "bar",
						data: data_daily.PR,
					},
				],
			};

			setDailyGraphConfig(optionDD);
			setPRGraphConfig(optionPr);
			setDailyDataReady(true);
		}
	}, [data_daily, solarLabel]);

	return (
		<>
			{selectedSite != null && (
				<Container fluid={true}>
					<Row>
						<Col xs={12} md={4}>
							<AnalyticDateRangePicker
								handleEvent={handleEvent}
								duration={period}
							/>
							<div>
								<h3 style={{ textAlign: "center", fontSize: "34px" }}>
									{selectedSite.label}
								</h3>
								<h4 style={{ textAlign: "center", fontSize: "24px" }}>
									Plant Capacity: {formatNumber(selectedSite.value.power, 2)} kW
								</h4>
								<h5 style={{ textAlign: "center", paddingTop: "5%" }}>
									{isGYUser && (
										<>
											<span>
												<i
													className="glyphicon glyphicon-dashboard"
													style={{
														verticalAlign: "top",
														marginRight: "5px",
													}}
												></i>
											</span>
											{selectedSite.value.contract_desc}
										</>
									)}
								</h5>
								{operationStatusDate}
							</div>
						</Col>
						<Col xs={12} md={8}>
							{mainGraphConfig != null && P10DataReady ? (
								<ReactEcharts
									style={{ height: "450px", padding: "20px 0px 20px 0px" }}
									option={mainGraphConfig}
									notMerge={true}
									lazyUpdate={true}
								/>
							) : (
								<LoaderGraph style={{ height: "450px" }} />
							)}
						</Col>
					</Row>
					<Row>
						<Col xs={12} md={7} className="graph-container">
							{(dailyGraphConfig && dailyDataReady) ? (
								<ReactEcharts
									style={{ height: "450px", padding: "20px 0px 20px 0px" }}
									option={dailyGraphConfig}
									notMerge={true}
									lazyUpdate={true}
								/>
							) : (
								<LoaderGraph style={{ height: "450px" }} />
							)}
						</Col>
						<Col xs={12} md={5} className="graph-container">
							{(PRGraphConfig && dailyDataReady) ? (
								<ReactEcharts
									style={{ height: "450px", padding: "20px 0px 20px 0px" }}
									option={PRGraphConfig}
									notMerge={true}
									lazyUpdate={true}
								/>
							) : (
								<LoaderGraph style={{ height: "450px" }} />
							)}
						</Col>
					</Row>
					<Row>
						<Col xs={12} md={12} className="graph-container">
							<div className={styles.toggleChartContainer}>
								<AnalyticAllDataGraph toggleCharts={toggleCharts} selectedChart={selectedChart} setSelectedChart={setSelectedChart} />
								<AnalyticProductionDataGraph toggleCharts={toggleCharts} selectedChart={selectedChart} setSelectedChart={setSelectedChart} />
							</div>
						</Col>
					</Row>
					<Row>
						<Col xs={12} md={12} className="graph-container">
							{mainGraphConfig != null && P10DataReady ? (
								<ReactEcharts
									style={{
										height: "450px",
										padding: "20px 0px 20px 0px",
										width: "80%",
										margin: "auto",
									}}
									option={phaseGraphConfig}
									notMerge={true}
									lazyUpdate={true}
								/>
							) : (
								<LoaderGraph style={{ height: "450px" }} />
							)}
						</Col>
					</Row>

					{user.roles.includes("analytics:AnalyticIndeGraphs:_view") && (
						<AnalyticIndeGraphs
							dataMonthly={dataIndeMonthly}
							dataDaily={dataIndeDaily}
						/>
					)}
				</Container>
			)}
		</>
	);
}

export default AnalyticContents;
